import { css, useTheme } from "@emotion/react";
import { isEmpty, isEqual } from "lodash";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import Wrapper from "core/Components/Wrapper";
import BannerForAdvertisement from "core/Components/BannerForAdvertisement/BannerForAdvertisement";
import PageSection from "pages/Components/ListPage/PageSection";
import Loading from "core/Components/Utils/Loading";
import PageHeaderAndDropdownsContainer
    from "pages/Components/ProductListPage/components/PageHeaderAndDropdownsContainer";
import DropdownContainer from "pages/Components/ProductListPage/components/DropdownContainer";
import DropdownButton from "core/Components/Buttons/DropdownButton/DropdownButton";
import {
    DROPDOWN_BACKGROUND_COLOUR,
    PRODUCT_GROUP_INIT_OPTION,
    PRODUCT_PRICE_RANGE_OPTIONS,
    PRODUCT_SORT_OPTIONS, PRODUCT_SUITABILITY_INIT_OPTION,
    SORT_DROPDOWN_OPTION_BACKGROUND_COLOUR,
    SORT_DROPDOWN_OPTION_BACKGROUND_HOVERED_COLOUR
} from "pages/Components/ProductListPage/includes/constants";
import ProductsSection from "pages/Components/ProductListPage/components/ProductsSection";
import NoData from "core/Components/Errors/NoData";
import ProductContainer from "pages/Components/ProductListPage/components/ProductContainer";
import DiscountedPriceAndProductImageContainer
    from "pages/Components/ProductListPage/components/DiscountedPriceAndProductImageContainer";
import DiscountedPriceImage from "pages/Components/ProductListPage/components/DiscountedPriceImage";
import ProductImageContainer from "pages/Components/ProductListPage/components/ProductImageContainer";
import ProductText from "pages/Components/ProductListPage/components/ProductText";
import ContentSection from "core/Components/ContentSection";
import Price from "pages/Components/ProductListPage/components/Price";
import Description from "pages/Components/ProductListPage/components/Description";
import BorderForSpaces from "pages/Components/ProductListPage/components/BorderForSpaces";
import ContentCombine from "core/Components/ContentCombine";
import Disclaimer from "pages/Components/ProductListPage/components/Disclaimer";
import SectionContainer from "core/Components/SectionContainer";
import useListBreakpoint from "pages/Components/ListPage/hooks/useListBreakpoint";
import useBreakpoint from "core/hooks/useBreakpoint";
import useSetState from "core/hooks/useSetState";
import { isOnly, isUp } from "theme/breakpoints";
import useProductListPageData from "pages/Components/ProductListPage/hooks/useProductListPageData";
import { useAppState } from "core/contexts/AppContext";
import useCart from "pages/Components/CartPage/hooks/useCart";
import Image from "core/Components/Image";
import ConfirmationModal from "pages/Components/CartAndCheckout/components/ConfirmationModal";
import { DEFAULT_COUNTRY_CODE_CONFIG } from "core/includes/constants";
import ProductItemCartButton from "pages/Components/ProductListPage/components/ProductItemCartButton";

type DefaultState = {
    minPriceRange?: number,
    maxPriceRange?: number,
    productGroup: Option,
    productSuitability: Option,
    priceRange: Option,
    sort: Option,
    sortField?: string,
    sortDirection?: string,
    isSubmitting: boolean,
    cardErrorMessage: string,
    showConfirmationCartModal: boolean,
}

const INITIAL_STATE = {
    productGroup: PRODUCT_GROUP_INIT_OPTION,
    productSuitability: PRODUCT_SUITABILITY_INIT_OPTION,
    priceRange: PRODUCT_PRICE_RANGE_OPTIONS[0],
    sort: PRODUCT_SORT_OPTIONS[0],
    sortField: PRODUCT_SORT_OPTIONS[0].value,
    sortDirection: "DESC",
    isSubmitting: false,
    cardErrorMessage: "",
    showConfirmationCartModal: false,
}

type Props = {
    page: ProductListPage,
};

type PriceRangeType = {
    value: string,
    label: string,
}

const ProductList = ({ page }: Props) => {
    const theme = useTheme();
    const [appState] = useAppState();
    const { smallBannerHeight } = useListBreakpoint();
    const { breakpoint } = useBreakpoint();
    const navigate = useNavigate();

    const [state, setState] = useSetState<DefaultState>(INITIAL_STATE);

    const disclaimer = `Copyright Notice: The resources acquired from Writer's Toolbox are
        copyright protected, and any unauthorised making, usage, distribution, importation, or sale by third parties requires
        the explicit consent of the patent owner.`;

    const [image, disclaimerPadding] = useMemo(() => {
        switch (true) {
            case isUp(breakpoint, '3xl'):
                return [page.landscape || page.portrait, '35px 300px', 180];
            case isOnly(breakpoint, 'xxl'):
                return [page.landscape || page.portrait, '35px 270px', 180];
            case isOnly(breakpoint, 'xl'):
                return [page.landscape || page.portrait, '35px 240px', 180];
            case isOnly(breakpoint, 'lg'):
                return [page.landscape || page.portrait, '35px 159px', 180];
            case isOnly(breakpoint, 'md'):
                return [page.portrait || page.landscape, '35px 45px', 164];
            case isOnly(breakpoint, 'sm'):
                return [page.portrait || page.landscape, '35px 36px', 164];
            default:
                return [page.portrait || page.landscape, '24px', 164];
        }
    }, [breakpoint, page]);

    const useAFocusPoint = (isEqual(image, page.landscape) && page.useFocusPoint) ||
        (isEqual(image, page.portrait) && page.useFocusPointPortrait);

    const {
        productList,
        loading,
        productGroupOptions,
        productSuitabilityOptions,
        countryCode, // CountryCode data object see CountryCode.php
    } = useProductListPageData(
        page,
        state.minPriceRange,
        state.maxPriceRange,
        state.productGroup,
        state.productSuitability,
        state.sortField,
        state.sortDirection,
    );

    const {
        updateCart
    } = useCart();

    const currency = useMemo(() => countryCode.currency || DEFAULT_COUNTRY_CODE_CONFIG.currency, [countryCode]);
    const taxPrefix = useMemo(() => page.isTaxIncluded ? 'incl.' : 'excl.', [page.isTaxIncluded]);

    const setPriceRange = (priceRange: Option) => {
        const { value } = priceRange;
        const minPriceRange = value.split("/")?.[0];
        const maxPriceRange = value.split("/")?.[1];

        setState({ minPriceRange, maxPriceRange, priceRange });
    };

    const setSortOption = (sort: Option) => {
        const { value } = sort;
        const sortField = value.split("/")?.[0];
        const sortDirection = value.split("/")?.[1] || "DESC";

        setState({ sort, sortField, sortDirection });
    };

    const altText = page.altText || '';

    const priceRangeOptions: PriceRangeType[] = useMemo(() => {
        if (currency === "$") return PRODUCT_PRICE_RANGE_OPTIONS;
        const newOptions: PriceRangeType[] = [];
        PRODUCT_PRICE_RANGE_OPTIONS.forEach((option: { value: string, label: string }) => {
            newOptions.push({
                ...option,
                label: option.label.replaceAll("$", currency)
            });
        });

        return newOptions;
    }, [currency]);

    const updateItemQuantity = (productId: string, productQuantity: number) => {
        if (appState.cart && appState.cart.countryCode !== appState.countryCode.code) {
            return new Promise((resolve) => resolve(setState({ showConfirmationCartModal: true })));
        } else {
            return updateCart({ productId: parseInt(productId), productQuantity: productQuantity });
        }
    }

    const closeCountryCheckoutPopup = useCallback(() => {
        setState({ showConfirmationCartModal: false });
    }, [setState]);

    const onCart = useCallback(() => {
        closeCountryCheckoutPopup();
        navigate('/cart-summary')
    }, [closeCountryCheckoutPopup, navigate]);

    const navigateToProduct = (item: ProductItem) => {
        navigate(`/classroom-resources/${item.uRLSegment}`);
    }

    const wrapperStyle = css`
        height: 100%;
    `;

    const descriptionStyle = css`
        margin-top: 50px;
    `;

    const productContainerStyle = css`
        :hover {
            z-index: 1;
            outline: 1px solid;
            box-shadow: 0 3px 10px #00000030;

            .product-title {
                text-decoration: underline;
            }
        }

        .quantity-button, .add-to-cart-button {
            margin-top: 15px !important;
        }
    `;

    const countryConfirmationModalStyle = css`
        .confirmation-modal-content {
            margin: auto 0;
        }
    `;

    const outOfStockStyle = css`
        position: absolute;
        top: 3px;
        font-size: 16px;
        font-weight: ${theme.fonts.weights.bold};
        color: ${theme.colours.redOrange};

        ${theme.breakpoints.down("xl")} {
            font-size: 14px;
            line-height: 23px;
        }
    `;

    const dropdownWrapperStyle = css`
        ${theme.breakpoints.up("lg")} {
            justify-content: end;
        }
    `;

    const dropdownContainerStyle = css`
        padding-top: 0 !important;
    `;

    const pageSectionStyle = css`
        padding-top: 20px !important;
    `;

    const productSectionStyle = css`
        margin: 0 !important;
        padding-top: 10px;

        ${theme.breakpoints.up("md")} {
            padding-top: 20px;
        }
    `;

    return (
        <SectionContainer data={{ backgroundColour: "white" }} overflowHidden>
            <Wrapper useMaxWidth={false} flexDirection={"column"} justifyContent={"start"} css={wrapperStyle}>
                {page.landscape && (
                    <BannerForAdvertisement
                        type={"success-story"}
                        mainBannerImage={image}
                        useMainBannerFocusPoint={useAFocusPoint}
                        bannerHeight={smallBannerHeight}
                        showButton={false}
                        useDivForFocusPoint={false}
                        customAltText={altText}
                    />
                )}

                <PageSection type={'success-story'} useSmallPadding css={pageSectionStyle}>
                    {(loading || (isEmpty(appState.cart) && appState.isCartLoading) ? (
                        <Loading onTop fitToScreen overlay overlayColour={"white"} delay={500} />
                    ) : (
                        <>
                            <PageHeaderAndDropdownsContainer css={dropdownWrapperStyle}>
                                <DropdownContainer css={dropdownContainerStyle}>
                                    <DropdownButton
                                        options={productGroupOptions}
                                        onChange={(item) => setState({ productGroup: item })}
                                        selectedValue={[state.productGroup]}
                                        backgroundColour={DROPDOWN_BACKGROUND_COLOUR}
                                        zIndex={theme.zIndex.zIndexMediumHigh}
                                        useSmallDropdownBox
                                    />

                                    <DropdownButton
                                        options={priceRangeOptions}
                                        onChange={setPriceRange}
                                        selectedValue={[state.priceRange]}
                                        backgroundColour={DROPDOWN_BACKGROUND_COLOUR}
                                        zIndex={theme.zIndex.zIndexMediumHigh}
                                        useSmallDropdownBox
                                    />

                                    <DropdownButton
                                        options={productSuitabilityOptions}
                                        onChange={(item) => setState({ productSuitability: item })}
                                        selectedValue={[state.productSuitability]}
                                        backgroundColour={DROPDOWN_BACKGROUND_COLOUR}
                                        useSmallDropdownBox
                                    />

                                    <DropdownButton
                                        options={PRODUCT_SORT_OPTIONS}
                                        onChange={(item) => setSortOption(item)}
                                        selectedValue={[state.sort]}
                                        backgroundColour={DROPDOWN_BACKGROUND_COLOUR}
                                        optionBackgroundColour={SORT_DROPDOWN_OPTION_BACKGROUND_COLOUR}
                                        optionBackgroundHoveredColour={SORT_DROPDOWN_OPTION_BACKGROUND_HOVERED_COLOUR}
                                        useSmallDropdownBox
                                    />
                                </DropdownContainer>
                            </PageHeaderAndDropdownsContainer>

                            <ProductsSection isNoData={isEmpty(productList)} css={productSectionStyle}>
                                {isEmpty(productList) && (
                                    <NoData />
                                )}

                                {productList?.map((product: ProductItem, index: number) => {
                                    const shouldDiscount = product.hasOnGoingDiscount && !!product.price.discountedPrice;
                                    const isOutOfStock = product.outOfStock || !product.availableForSale ||
                                        (product.inventoryQuantity !== undefined && product.inventoryQuantity <= 0);

                                    const description = (product.shortDescription || product.description);

                                    return (
                                        <ProductContainer
                                            key={`${product.id}-${index}`}
                                            index={index}
                                            onClick={() => navigateToProduct(product)}
                                            css={productContainerStyle}
                                        >
                                            {isOutOfStock && (
                                                <ContentCombine css={outOfStockStyle}>
                                                    Temporarily out of stock
                                                </ContentCombine>
                                            )}
                                            <DiscountedPriceAndProductImageContainer>
                                                <DiscountedPriceImage
                                                    price={product.price}
                                                    shouldDiscount={shouldDiscount}
                                                    currency={currency}
                                                    taxLabel={countryCode?.taxLabel}
                                                    taxPrefix={taxPrefix}
                                                />
                                                <ProductImageContainer>
                                                    {product.imagesAndVideos[0] && (
                                                        <Image
                                                            image={product.imagesAndVideos[0].image}
                                                            useFocusPoint={product.imagesAndVideos[0].useFocusPoint}
                                                        />
                                                    )}
                                                </ProductImageContainer>
                                            </DiscountedPriceAndProductImageContainer>

                                            <ProductText text={product.title} className={'product-title'} />

                                            <ContentSection
                                                height={'unset'}
                                                alignItems={'center'}
                                                flexDirection={'column'}
                                            >
                                                <Price
                                                    price={product.price}
                                                    shouldDiscount={shouldDiscount}
                                                    currency={currency}
                                                    taxLabel={countryCode?.taxLabel}
                                                    taxPrefix={taxPrefix}
                                                />
                                            </ContentSection>

                                            <Description
                                                expectedDate={product.expectedDate}
                                                description={description}
                                                isCentred={!!product.shortDescription}
                                            />
                                            <ProductItemCartButton
                                                product={product}
                                                updateItemQuantity={updateItemQuantity}
                                            />
                                        </ProductContainer>
                                    )
                                })}

                                <BorderForSpaces productList={productList} />
                            </ProductsSection>

                            {!!page.description && (
                                <ContentCombine
                                    Tag={'p'}
                                    fontSizeType={'9'}
                                    anchorColour={theme.colours.curiousBlue}
                                    css={descriptionStyle}
                                >
                                    {page.description}
                                </ContentCombine>
                            )}

                            <Disclaimer padding={disclaimerPadding}>
                                {disclaimer}
                            </Disclaimer>
                        </>
                    ))}

                </PageSection>

                {state.showConfirmationCartModal && (
                    <ConfirmationModal
                        message={
                            <>
                                <div>
                                    Oops! &nbsp;This product doesn’t match your region.
                                </div>
                                <br />
                                <div>
                                    Please check out your cart first, then change country to browse products in your
                                    region.
                                </div>
                            </>
                        }
                        confirmLabel={"Go to cart"}
                        onCancel={closeCountryCheckoutPopup}
                        onConfirm={onCart}
                        css={countryConfirmationModalStyle}
                    />
                )}
            </Wrapper>
        </SectionContainer>
    )
}

export default ProductList;